import React, {useCallback} from 'react';
import {CSSTransition} from 'react-transition-group';
import {
    Draggable,
    Flex,
    Icon,
    PriorityIcon,
    Text,
    Scrollbar
} from '@priority-software/priority-style-react/dist';
import cx from 'classnames';
import DraggableList, {InjectedDraggableProps} from '../../../Common/DraggableList';
import {getTopDialogZindex} from '../../../../_shared/GWTBridge/invoke/priModalDlg';
import texts from '../../../../features/MoreInfo/texts';
import styles from './styles.module.scss';
import './animation.scss';
import {Tooltip} from '../../../Common/Tooltip';
import {isRTL} from '../../../../Localization';

type Props = {
    data: Array<any>;
    deleteItem: (id) => void;
    onReorder: (values, i: number) => void;
};

export const SelectedFields = (props: Props) => {
    const {data, deleteItem, onReorder} = props;
    const isRequired = data.length === 1;
    const rtl = isRTL();

    return (
        <Scrollbar autoHeight autoHeightMax={345} rtl={rtl} shiftVerticalScrollPositionX={12}>
            <div className={styles.component}>
                <DraggableList
                    data={data}
                    handleDNDFinish={onReorder}
                    renderItem={({item, ...draggableProps}) => (
                        <CSSTransition
                            key={item.id}
                            in={true}
                            appear={true}
                            timeout={1000}
                            classNames="fade"
                            unmountOnExit
                        >
                            <Item
                                deleteItem={deleteItem}
                                isRequired={isRequired}
                                {...item}
                                {...draggableProps}
                                key={item.id}
                            />
                        </CSSTransition>
                    )}
                />
            </div>
        </Scrollbar>
    );
};

type ItemProps = {
    deleteItem: (id: number) => void;
    label: string;
    id: number;
    index: number;
    fixed: boolean;
    isRequired: boolean;
} & InjectedDraggableProps;

export const Item = (props: ItemProps) => {
    const {isRequired, label, id, deleteItem, index, ...draggableProps} = props;

    const onRemove = useCallback(() => {
        if (!isRequired) deleteItem(id);
    }, [deleteItem, id, isRequired]);

    return (
        <Draggable
            {...draggableProps}
            componentClasses={cx(styles.selectedItemWrap)}
            iconPosition="center"
        >
            <div className={styles.index}>{index + 1}</div>

            <Flex
                componentClasses={cx(styles.selectedItem, {
                    [styles.noHover]: isRequired
                })}
            >
                <Text tag="div" skin="paragraph-2" textColor="primary-700">
                    {label}
                </Text>

                <Tooltip
                    zIndex={getTopDialogZindex()}
                    tooltip={isRequired && texts.empty_selected_msg}
                    delay={200}
                >
                    <Icon
                        icon={PriorityIcon.closeSmall}
                        iconColor={isRequired ? 'grey-light' : 'secondary-400'}
                        onClick={onRemove}
                    />
                </Tooltip>
            </Flex>
        </Draggable>
    );
};
